import _get from 'lodash/get';
import _has from 'lodash/has';
import _memoize from 'lodash/memoize';
import _set from 'lodash/set';

import { createAction, createCachedSelector, createSelector, DuckSelector } from '@atc/modular-redux';

import { applyFilterGroups, filtersDuckCreator } from 'axl-ducks';

import {
    excludedFilters,
    formatChipLabel,
    getMileageLabel,
    getPriceLabel,
    getYearLabel,
    showHoverText,
} from '@/utilities/filterChipUtil';

import { srpDuckPreset } from '@/ducks/presets/index.js';

import srpSelectedFiltersInOrderDuck from './srpSelectedFiltersInOrderDuck';

const DEFAULT_EMPTY_OBJECT = {};
const DEFAULT_EMPTY_ARRAY = [];
const DEFAULT_EMPTY_STRING = '';

// derive a filter's metadata
// Currently handles filter names with |  but we need to update the filter name delimiter to something more unique
export class FilterName {
    constructor(filterName) {
        this.original = filterName;
        const [key, ...rest] = filterName.split('|');
        this.segments = [key, rest.join('|')];
        this.hasGroup = rest.length > 0;
        this.group = this.hasGroup && key;
        this.name = this.hasGroup ? this.segments[1] : key;
    }
}

const createFilterName = _memoize((filterName) => new FilterName(filterName));

// verify filter value is not empty string or empty array
const isNotEmptyValue = (value) => (typeof value === 'string' && value !== '') || (typeof value === 'object' && value.length > 0);

// cache for ignored keys to avoid repeated array creation
const IGNORED_KEYS = new Set([
    'modelCodeList',
    'modelCode',
    'seriesCodeList',
    'seriesCode',
    'trimCodeList',
    'trimCode',
    'packages',
]);

const getOptions = ({
    filters = {},
    filterGroups = {},
    placeholders = {},
    activeExpansion,
    lastTouched,
    prevFilterOptions = {},
}) => {
    // build a new filterOptions data set from each filter data source
    const filterOptions = {
        filters: {
            ...filters,

            // update maxPrice with placeholder
            maxPrice: {
                ...filters.maxPrice,
                placeholder: placeholders.maxPrice,
            },

            // update minPrice with placeholder
            minPrice: {
                ...filters.minPrice,
                placeholder: placeholders.minPrice,
            },

            // update makeCode.collapsed to not collapse if it was the last touched filter
            makeCode: {
                ...filters.makeCode,
                collapsed: (lastTouched === 'makeCode') ? false : _get(filters, 'makeCode.collapsed', true),
            },

        },
        filterGroups,
    };

    if (lastTouched && activeExpansion && lastTouched === activeExpansion) {

        // get filter name metadata
        const lastTouchedFilter = createFilterName(lastTouched);

        // Determine the correct path to the active filter data in the prevFilterOptions and new filterOptions
        const currentFilterPath = lastTouchedFilter.hasGroup ? [lastTouchedFilter.group, lastTouchedFilter.original] : [lastTouchedFilter.original];
        const filterOptionsPath = lastTouchedFilter.hasGroup ? ['filterGroups', lastTouchedFilter.group, lastTouchedFilter.name] : ['filters', lastTouchedFilter.name];

        // Retrieve the data for the active filter in the prevFilterOptions and new filterOptions
        const currentActiveFilterData = _get(prevFilterOptions, currentFilterPath, {});

        // We want to set these values from the new search options response on top of the old one
        // so we destructure them from the new search options response, so we can reassign later
        const { optionsSubIndex, value } = _get(filterOptions, filterOptionsPath, {});

        // Current API response for base filters options returns zero counts and disables all inputs beside selected values
        // due to this we need to update the filter options response to ignore all the data it has received besides the updated optionsSubIndex
        // and re-use the existing state of the active filter's options sub index
        _set(filterOptions, filterOptionsPath, {
            ...currentActiveFilterData,
            // set new sub-index and value
            optionsSubIndex,
            value,
        });

    }

    // Apply the filterGroups to generate the options
    return applyFilterGroups(filterOptions);
};

const getValues = ({
    options,
    query,
}) => {
    const values = Object.keys(options).reduce((acc, optionKey) => {

        // If filterOption has a value key then we assume that it is not a multi-filter (trim, model, etc...) or an aggregation filter
        // we set the value if defined by the option response or from the request params
        const hasValue = _has(options, [optionKey, 'value']);

        if (hasValue) {

            let value = _get(options, [optionKey, 'value'], '');

            if (optionKey === 'makeCode') {
                value = options.makeCode.value.sort((a, b) => query.makeCode.indexOf(a) - query.makeCode.indexOf(b));
            }

            // to maintain the smallest state possible only populate values if they exist
            if (isNotEmptyValue(value)) {
                // Assign filter value
                acc[optionKey] = { value };

                // only add protected flag when it is true
                const isProtected = _get(options, [optionKey, 'protected'], false);
                if (isProtected) {
                    acc[optionKey].protected = true;
                }
            }
        } else {
            // iterate over each multi-filter and set the value directly from the option response
            Object.keys(options[optionKey]).forEach((multiFilterKey) => {
                const multiFilter = _get(options, [optionKey, multiFilterKey], {});
                if (multiFilter.name && multiFilter.value && isNotEmptyValue(multiFilter.value)) {
                    acc[multiFilter.name] = { value: multiFilter.value };
                }
            });
        }

        return acc;
    }, {});

    const filtersOptionsKeys = Object.keys(options);

    // Iterate over passed in query and set any filter values that have not been previously defined
    Object.entries(query).forEach(([key, value]) => {
        // ensure the key from the payload is actually a filter key
        if ((filtersOptionsKeys.includes(key)
            && !Object.prototype.hasOwnProperty.call(values, key)
            && !IGNORED_KEYS.has(key))
            || (key === 'experience' && !Object.prototype.hasOwnProperty.call(values, key))
        ) {
            values[key] = { value };
        }
    });

    return values;
};

const setOtherFilters = (filterValue, filterOptions, filterKey, acc) => {
    filterValue.forEach((key) => {
        let filterValueOptions;

        if (filterOptions[filterKey]) {
            filterValueOptions = filterOptions[filterKey];
        } else {
            const pipeIndex = filterKey.indexOf('|');
            if (pipeIndex !== -1) {
                const mainKey = filterKey.substring(0, pipeIndex);
                if (filterOptions[mainKey] && filterOptions[mainKey][filterKey]) {
                    filterValueOptions = filterOptions[mainKey][filterKey];
                }
            }
        }

        if (filterValueOptions) {
            const filterValueOption = filterValueOptions.options && filterValueOptions.options.find(({ value }) => value === key);
            if (filterValueOption) {
                const filterLabel = filterValueOption.label;
                const label = formatChipLabel(filterLabel || key, filterKey, filterValueOption.value);
                acc.push({
                    label,
                    title: showHoverText(filterLabel || key),
                    filterName: filterKey,
                    value: filterValueOption.value,
                });
            }
        }
    });
};

const setMileageFilter = (filterOptions, filterValue, filterKey, acc) => {
    const maxMileageLabel = getMileageLabel(filterOptions, filterValue, filterKey);
    if (maxMileageLabel) {
        acc.push({
            label: maxMileageLabel,
            filterName: filterKey,
            value: filterValue,
        });
    }
};

const setPriceFilter = (filterKey, acc, selectedFilterValues) => {
    if (['minPrice', 'maxPrice'].includes(filterKey)) {
        if (acc.filter((key) => key.filterName === 'minPrice').length === 0
            && acc.filter((key) => key.filterName === 'maxPrice').length === 0) {
            const label = getPriceLabel(selectedFilterValues);
            acc.push({
                label,
                filterName: filterKey,
                value: selectedFilterValues,
            });
        }
    }
};

const setYearFilter = (filterKey, filterValue, acc, selectedFilterValues) => {
    if (['startYear', 'endYear'].includes(filterKey)) {
        if (filterValue && parseInt(filterValue, 10) !== 0
            && (acc.filter((key) => key.filterName === 'startYear').length === 0 && acc.filter((key) => key.filterName === 'endYear').length === 0)) {
            const label = getYearLabel(selectedFilterValues);
            acc.push({
                label,
                filterName: filterKey,
                value: selectedFilterValues,
            });
        }
    }
};

const setFilters = (filterKey, filterValue, filterOptions, acc) => {
    if (!excludedFilters.includes(filterKey)) {
        if (Array.isArray(filterValue) && !['maxMileage', 'keywordPhrases', 'productTypes'].includes(filterKey)) {
            setOtherFilters(filterValue, filterOptions, filterKey, acc);
        } else if (['maxMileage'].includes(filterKey)) {
            setMileageFilter(filterOptions, filterValue, filterKey, acc);
        } else {
            const label = formatChipLabel(filterValue, filterKey);
            acc.push({
                label,
                title: showHoverText(filterValue),
                value: filterValue,
                filterName: filterKey,
            });
        }
    }
};

const srpFiltersDuck = filtersDuckCreator({
    ...srpDuckPreset,
    initialState: {
        visible: false,
        initialized: false,
        activeExpansions: ['location', 'vehicleExchange', 'yearRange', 'makeCode', 'priceRange', 'listingType',
            'homeServices', 'dealType', 'vehicleType'],
    },
}).extend({
    types: [
        'SET_SRP_FILTERS',
        'SET_COLLAPSED_SRP_FILTERS',
    ],
    reducer: (state, action, { types }) => {
        switch (action.type) {

            case types.SET_SRP_FILTERS: {
                const {
                    filters,
                    filterGroups,
                    placeholders = {},
                    query,
                } = action.payload;

                const {
                    activeExpansion,
                    lastTouched,
                    options: prevFilterOptions,
                } = state;

                const options = getOptions({
                    filters,
                    filterGroups,
                    placeholders,
                    activeExpansion,
                    lastTouched,
                    prevFilterOptions,
                });

                const values = getValues({
                    options,
                    query,
                });

                return {
                    ...state,
                    initialized: true,
                    options,
                    values,
                };
            }

            // Collapse all filters except the ones passed in the payload
            case types.SET_COLLAPSED_SRP_FILTERS: {
                return {
                    ...state,
                    activeExpansions: action.payload,
                };
            }

            default:
                return state;
        }
    },
    creators: ({ types }) => ({
        // TODO: BONNET NEXT - What is going on here?
        show: () => { },
        // TODO: BONNET NEXT - What is going on here?
        hide: () => { },
        setSrpFilters: createAction(types.SET_SRP_FILTERS),
        setCollapsedSrpFilters: createAction(types.SET_COLLAPSED_SRP_FILTERS),
    }),
    selectors: () => ({
        hasFiltersOptions: new DuckSelector((selectors) => createSelector(
            selectors.getFiltersOptions,
            (options) => Object.keys(options).length > 0,
        )),
        getFiltersKeys: new DuckSelector((selectors) => createSelector(
            selectors.getFiltersOptions,
            (options) => Object.keys(options),
        )),
        getLastModified: new DuckSelector((selectors) => createSelector(
            selectors.getLocalState,
            (state) => state.lastTouched && state.lastTouched.split('|')[0]
        )),
        hasSingleSelection: new DuckSelector((selectors) => createSelector(
            selectors.getFiltersValues,
            (values = {}) => Object.entries(values).reduce((acc, [key, value]) => ({
                ...acc,
                [key]: [].concat(value).length === 1,
            }), {})
        )),
        hasMultipleSelections: new DuckSelector((selectors) => createSelector(
            selectors.getFiltersValues,
            (values = {}) => Object.entries(values).reduce((acc, [key, value]) => ({
                ...acc,
                [key]: [].concat(value).length > 1,
            }), {})
        )),
        isMultiple: new DuckSelector((selectors) => createSelector(
            selectors.getFiltersOptions,
            (options = {}) => Object.entries(options).reduce((acc, [key, value]) => ({
                ...acc,
                [key]: Object.prototype.hasOwnProperty.call(value, 'options'),
            }), {})
        )),
        getFilterValueLabels: new DuckSelector((selectors) => createSelector(
            selectors.getLabelsByValues,
            selectors.getValuesState,
            (filtersValueLabels, selectedFilters) => {
                if (!selectedFilters || Object.keys(selectedFilters).length === 0) {
                    return {};
                }

                const result = {};

                for (const key in selectedFilters) {
                    if (Object.prototype.hasOwnProperty.call(selectedFilters, key)) {
                        const selectedFilter = selectedFilters[key];
                        if (selectedFilter) {
                            let label = '';
                            // Process selected filters with many values e.g. makeCodeList: ["CHEV", "FORD"]
                            if (Array.isArray(selectedFilter.value) && selectedFilter.value.length > 0) {
                                const valueLabel = {};
                                for (let i = 0; i < selectedFilter.value.length; i++) {
                                    const val = selectedFilter.value[i];
                                    label = _get(filtersValueLabels, [key, val], false);
                                    if (label) {
                                        valueLabel[val] = label;
                                    }
                                }
                                // Add if filter key not yet added and has values
                                if (Object.keys(valueLabel).length > 0) {
                                    result[key] = valueLabel;
                                }
                            } else {
                                label = _get(filtersValueLabels, [key, selectedFilter.value], false);

                                // minYear, maxYear are user entered values and won't be in search options
                                result[key] = label || selectedFilter.value;
                                if (label) {
                                    result[key] = { [selectedFilter.value]: label };
                                }
                            }
                        }
                    }
                }

                return result;
            }
        )),

        // get specific filter options and values
        getActiveExpansions: new DuckSelector((selectors) => (state) => _get(selectors.getDuckState(state), 'activeExpansions', DEFAULT_EMPTY_ARRAY)),
        isExpansionActive: new DuckSelector((selectors) => createCachedSelector(
            selectors.getActiveExpansions,
            (state, filterName) => filterName,
            (activeExpansions = [], filterName) => activeExpansions.includes(filterName)
        )((state, filterName) => filterName || 'default')),

        getFeatureFilterOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'featureCode', DEFAULT_EMPTY_OBJECT)),
        getFeatureFilterValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'featureCode', DEFAULT_EMPTY_ARRAY)),

        getSellerTypeOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'sellerType', DEFAULT_EMPTY_ARRAY)),
        getSellerTypeValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'sellerType', DEFAULT_EMPTY_ARRAY)),

        getListingTypeOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'listingType', DEFAULT_EMPTY_OBJECT)),
        getListingTypeValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'listingType', DEFAULT_EMPTY_ARRAY)),

        getLocationFilterOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'location', DEFAULT_EMPTY_OBJECT)),
        getLocationFilterValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'location', DEFAULT_EMPTY_STRING)),

        getMarketExtensionOption: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'marketExtension', DEFAULT_EMPTY_STRING)),
        getMarketExtensionValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'marketExtension', DEFAULT_EMPTY_STRING)),

        getMakeFilterOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'makeCode', DEFAULT_EMPTY_OBJECT)),
        getMakeFilterValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'makeCode', DEFAULT_EMPTY_ARRAY)),

        getModelFilterOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'modelCode', DEFAULT_EMPTY_OBJECT)),
        getModelFilterValues: new DuckSelector((selectors) => createSelector(
            selectors.getFiltersValues,
            (filtersValues) => {
                const modelValues = {};

                for (const key in filtersValues) {
                    if (Object.hasOwn(filtersValues, key) && key.includes('modelCode')) {
                        modelValues[key] = filtersValues[key];
                    }
                }
                return modelValues;
            }
        )),

        getSeriesFilterOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'seriesCode', DEFAULT_EMPTY_OBJECT)),
        getSeriesFilterValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'seriesCode', DEFAULT_EMPTY_ARRAY)),

        getSort: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'sort', DEFAULT_EMPTY_STRING)),

        getTrimFilterOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'trimCode', DEFAULT_EMPTY_ARRAY)),
        getTrimFilterValues: new DuckSelector((selectors) => createSelector(
            selectors.getFiltersValues,
            (filtersValues) => {
                const trimValues = {};

                for (const key in filtersValues) {
                    if (Object.prototype.hasOwnProperty.call(filtersValues, key) && key.includes('trimCode')) {
                        trimValues[key] = filtersValues[key];
                    }
                }
                return trimValues;
            }
        )),

        getPackageFilterOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'packages', DEFAULT_EMPTY_ARRAY)),
        getPackageFilterValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'packages', DEFAULT_EMPTY_ARRAY)),

        getFeaturesOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'featureCode', DEFAULT_EMPTY_ARRAY)),
        getFeaturesValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'featureCode', DEFAULT_EMPTY_ARRAY)),

        getPriceOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'priceRange', DEFAULT_EMPTY_OBJECT)),
        getMinPriceOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'minPrice', DEFAULT_EMPTY_OBJECT)),
        getMaxPriceOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'maxPrice', DEFAULT_EMPTY_OBJECT)),
        getMinPriceValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'minPrice', null)),
        getMaxPriceValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'maxPrice', null)),

        getSearchRadiusOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'searchRadius', DEFAULT_EMPTY_OBJECT)),
        getSelectedSearchRadius: new DuckSelector((selectors) => (state) => +_get(selectors.getFiltersValues(state), 'searchRadius', 0)),

        getZipOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'zip', DEFAULT_EMPTY_OBJECT)),
        getSelectedZipValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'zip', DEFAULT_EMPTY_STRING)),

        getYearOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'yearRange', DEFAULT_EMPTY_OBJECT)),
        getYearValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'yearRange', DEFAULT_EMPTY_STRING)),
        getYearEndOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'endYear', DEFAULT_EMPTY_OBJECT)),
        getYearEndValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'endYear', DEFAULT_EMPTY_STRING)),
        getYearStartOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'startYear', DEFAULT_EMPTY_OBJECT)),
        getYearStartValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'startYear', DEFAULT_EMPTY_STRING)),

        getMileageOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'mileage', DEFAULT_EMPTY_ARRAY)),
        getMileageValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'mileage', DEFAULT_EMPTY_STRING)),

        getDriveTypeOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'driveGroup', DEFAULT_EMPTY_ARRAY)),
        getDriveTypeValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'driveGroup', DEFAULT_EMPTY_ARRAY)),

        getVehicleHistoryOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'vehicleHistoryType', DEFAULT_EMPTY_ARRAY)),
        getVehicleHistoryValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'vehicleHistoryType', DEFAULT_EMPTY_ARRAY)),

        getHomeServicesOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'homeServices', DEFAULT_EMPTY_ARRAY)),
        getHomeServicesValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'homeServices', DEFAULT_EMPTY_ARRAY)),

        getPriceRatingOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'dealType', DEFAULT_EMPTY_ARRAY)),
        getPriceRatingValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'dealType', DEFAULT_EMPTY_ARRAY)),

        getBodyStyleOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'vehicleStyleCode', DEFAULT_EMPTY_OBJECT)),
        getBodyStyleValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'vehicleStyleCode', DEFAULT_EMPTY_STRING)),

        getBodyStyleSubtypeCodeOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'bodyStyleSubtypeCode', DEFAULT_EMPTY_OBJECT)),
        getBodyStyleSubtypeCodeValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'bodyStyleSubtypeCode', DEFAULT_EMPTY_ARRAY)),

        getTruckBedLengthOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'truckBedLength', DEFAULT_EMPTY_ARRAY)),
        getTruckBedLengthValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'truckBedLength', DEFAULT_EMPTY_ARRAY)),

        getSedanSizeSubtypeOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'sedanSubtypeCode', DEFAULT_EMPTY_ARRAY)),
        getSedanSizeSubtypeValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'sedanSubtypeCode', DEFAULT_EMPTY_ARRAY)),

        getSuvSizeSubtypeOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'suvSubtypeCode', DEFAULT_EMPTY_ARRAY)),
        getSuvSizeSubtypeValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'suvSubtypeCode', DEFAULT_EMPTY_ARRAY)),

        getVanSizeSubtypeOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'vanSubtypeCode', DEFAULT_EMPTY_ARRAY)),
        getVanSizeSubtypeValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'vanSubtypeCode', DEFAULT_EMPTY_ARRAY)),

        getTransmissionCodeOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'transmissionCode', DEFAULT_EMPTY_ARRAY)),
        getTransmissionCodeValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'transmissionCode', DEFAULT_EMPTY_ARRAY)),

        getEngineDisplacementOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'engineDisplacement', DEFAULT_EMPTY_ARRAY)),
        getEngineDisplacementValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'engineDisplacement', DEFAULT_EMPTY_ARRAY)),

        getEngineCodeOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'engineCode', DEFAULT_EMPTY_ARRAY)),
        getEngineCodeValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'engineCode', DEFAULT_EMPTY_ARRAY)),

        getExteriorColorOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'extColorSimple', DEFAULT_EMPTY_ARRAY)),
        getExteriorColorValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'extColorSimple', DEFAULT_EMPTY_ARRAY)),

        getInteriorColorOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'intColorSimple', DEFAULT_EMPTY_ARRAY)),
        getInteriorColorValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'intColorSimple', DEFAULT_EMPTY_ARRAY)),

        getFuelTypeOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'fuelTypeGroup', DEFAULT_EMPTY_ARRAY)),
        getFuelTypeValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'fuelTypeGroup', DEFAULT_EMPTY_ARRAY)),

        getFuelEconomyOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'mpgRange', DEFAULT_EMPTY_ARRAY)),
        getFuelEconomyValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'mpgRange', '0')),

        getSeatsOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'maximumSeating', DEFAULT_EMPTY_ARRAY)),
        getSeatsValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'maximumSeating', DEFAULT_EMPTY_ARRAY)),

        getDoorsOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'doorCode', DEFAULT_EMPTY_ARRAY)),
        getDoorsValue: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'doorCode', DEFAULT_EMPTY_ARRAY)),

        getElectricMileRangeOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'electricMileRange', DEFAULT_EMPTY_ARRAY)),
        getElectricMileRangeValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'electricMileRange', DEFAULT_EMPTY_ARRAY)),

        getVehicleExchangeOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'vehicleExchange', DEFAULT_EMPTY_ARRAY)),
        getVehicleExchangeValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'vehicleExchange', DEFAULT_EMPTY_ARRAY)),

        getVehicleTypeOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'vehicleType', DEFAULT_EMPTY_ARRAY)),
        getVehicleTypeValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'vehicleType', DEFAULT_EMPTY_ARRAY)),

        getVehicleUseTypeOptions: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersOptions(state), 'vehicleUseType', DEFAULT_EMPTY_ARRAY)),
        getVehicleUseTypeValues: new DuckSelector((selectors) => (state) => _get(selectors.getFiltersValues(state), 'vehicleUseType', DEFAULT_EMPTY_ARRAY)),

        getBuyOnlineFilterOptions: new DuckSelector((selectors) => createSelector(
            selectors.getHomeServicesOptions,
            (homeServicesOptions) => homeServicesOptions?.options?.find(({ value }) => value === 'BUY_ONLINE') || DEFAULT_EMPTY_ARRAY,
        )),
        isBuyOnlineSelected: new DuckSelector((selectors) => createSelector(
            selectors.getHomeServicesValues,
            (homeServicesValues) => homeServicesValues.includes('BUY_ONLINE'),
        )),
        isCpoSelected: new DuckSelector((selectors) => createSelector(
            selectors.getListingTypeValues,
            (listingTypeValues) => listingTypeValues.includes('CERTIFIED'),
        )),
        getCpoFilterOptions: new DuckSelector((selectors) => createSelector(
            selectors.getListingTypeOptions,
            (listingTypeValues) => listingTypeValues?.options?.find(({ value }) => value === 'CERTIFIED') || DEFAULT_EMPTY_ARRAY,
        )),

        // end get specific filter options and values

        getSelectedFilterChips: new DuckSelector((selectors) => createSelector(
            selectors.getFiltersValues,
            selectors.getFiltersOptions,
            (filterValues, filterOptions) => {
                if (!filterValues || Object.keys(filterValues).length === 0) {
                    return [];
                }

                const selectedFilterValues = { ...filterValues };

                // car bravo experience does require own filter chip
                if (selectedFilterValues.experience !== 'car-bravo') {
                    delete selectedFilterValues.experience; // Buy Online already shows unique filter chips. We do not want to show redundant experience chips
                }

                const result = [];

                for (const filterKey in selectedFilterValues) {
                    if (Object.prototype.hasOwnProperty.call(selectedFilterValues, filterKey)) {
                        const filterValue = selectedFilterValues[filterKey];
                        setFilters(filterKey, filterValue, filterOptions, result);
                        setPriceFilter(filterKey, result, selectedFilterValues);
                        setYearFilter(filterKey, filterValue, result, selectedFilterValues);
                    }
                }

                return result;
            }
        )),

        getSelectedFilterChipsInOrder: new DuckSelector((selectors) => createSelector(
            selectors.getSelectedFilterChips,
            srpSelectedFiltersInOrderDuck.selectors.getFilterChipsInOrder,
            (selectedFilterChips, filterChipsInOrder) => {

                if (filterChipsInOrder.length < 1) {
                    return selectedFilterChips;
                }

                // Helper function to create a unique key for chip comparison
                const createChipKey = (chip) => {
                    let chipValue = chip.value;
                    // For year range chips, the value is an object with startYear and endYear
                    if (['endYear', 'startYear'].includes(chip.filterName)) {
                        // If both startYear and endYear exist, create a range key
                        if (typeof chipValue === 'object' && chipValue.startYear && chipValue.endYear) {
                            chipValue = `${chipValue.startYear}-${chipValue.endYear}`;
                        } else {
                            chipValue = chipValue[chip.filterName];
                        }
                    }
                    return `${chip.filterName}-${chipValue}`;
                };

                // Use Map for faster lookups instead of array.find
                const selectedChipsMap = new Map();
                selectedFilterChips.forEach((chip) => {
                    const key = createChipKey(chip);
                    selectedChipsMap.set(key, chip);
                });

                const orderChipsMap = new Map();
                filterChipsInOrder.forEach((chip) => {
                    const key = createChipKey(chip);
                    orderChipsMap.set(key, chip);
                });

                // Find additions and subtractions more efficiently
                const additionalFilters = [];
                const subtractedFilters = [];

                // Check for additions
                for (const chip of selectedFilterChips) {
                    const key = createChipKey(chip);
                    if (!orderChipsMap.has(key)) {
                        additionalFilters.push(chip);
                    }
                }

                // Check for subtractions
                for (const chip of filterChipsInOrder) {
                    const key = createChipKey(chip);
                    if (!selectedChipsMap.has(key)) {
                        subtractedFilters.push(chip);
                    }
                }

                let orderedFilterValues = [...filterChipsInOrder];

                // Clean removed filters
                if (subtractedFilters.length > 0) {
                    const subtractedSet = new Set(subtractedFilters.map(createChipKey));
                    orderedFilterValues = filterChipsInOrder.filter((chip) => {
                        const key = createChipKey(chip);
                        return !subtractedSet.has(key);
                    });
                }

                // Always add most recent filter(s) first
                if (additionalFilters.length > 0) {
                    orderedFilterValues = [...additionalFilters, ...orderedFilterValues];
                }

                return orderedFilterValues;
            }
        )),

        isTruckSelected: new DuckSelector((selectors) => createSelector(
            selectors.getBodyStyleOptions,
            selectors.getBodyStyleValues,
            selectors.getModelFilterValues,
            (bodyStyleOptions, bodyStyleValues, modelValues) => {
                const isTruckBodyStyle = bodyStyleValues.includes('TRUCKS');

                const availableTruckOptions = bodyStyleOptions?.options?.filter(({ value }) => value === 'TRUCKS');
                const isTruckModel = Object.values(modelValues)?.length > 0 && availableTruckOptions?.length > 0;

                return isTruckBodyStyle || isTruckModel;
            }
        )),

        isSUVSelected: new DuckSelector((selectors) => createSelector(
            selectors.getBodyStyleOptions,
            selectors.getBodyStyleValues,
            selectors.getModelFilterValues,
            (bodyStyleOptions, bodyStyleValues, modelValues) => {
                const isSUVBodyStyle = bodyStyleValues.includes('SUVCROSS');

                const availableSUVOptions = bodyStyleOptions?.options?.filter(({ value }) => value === 'SUVCROSS');
                const isSUVModel = Object.values(modelValues)?.length > 0 && availableSUVOptions?.length > 0;

                return isSUVBodyStyle || isSUVModel;
            }
        )),
    }),
});

export default srpFiltersDuck;
